import type { Path } from '@cobliteam/api-lib/src/__generated__/graphql';
import { useGetImages } from '@cobliteam/hooks/src/useCamera/useFaceId';
import { useTranslation } from '@cobliteam/i18n';
import Image from '@cobliteam/ui/components/Image';
import { TableCell } from '@cobliteam/ui/components/Table';
import Tooltip from '@cobliteam/ui/components/Tooltip';
import Typography from '@cobliteam/ui/components/Typography';
import Icon from '@cobliteam/ui/icons';
import { DriverImagesCarousel } from '@cobliteam/ui-business/components';
import SadFace from '@cobliteam/ui-business/icons/face-id/sad-face.svg?react';

interface PhotosColumnProps {
  path: Path;
}

const PhotosColumn = ({ path }: PhotosColumnProps) => {
  const { startTime, endTime, vehicle } = path;
  const { t } = useTranslation('path-association');

  const { data: images = { content: [] }, isLoading } = useGetImages(vehicle?.id || '', {
    fromTimestamp: new Date(startTime).toISOString(),
    toTimestamp: new Date(endTime).toISOString(),
    page: 0,
    size: 100,
  });

  if (isLoading) {
    return (
      <div className='flex aspect-video w-36 flex-col items-center justify-center gap-1 rounded-md bg-gray-5 text-gray-8'>
        <Icon name='cb-loading-spin' />
      </div>
    );
  }

  if (images?.content.length === 0) {
    return (
      <Tooltip content={t('columns.photo_unavailable_tooltip')}>
        <div className='flex aspect-video w-36 flex-col items-center justify-center gap-1 rounded-md bg-gray-5 text-gray-8'>
          <SadFace className='size-4' />
          <Typography variant='caption-default' className='text-gray-8'>
            {t('columns.no_photos')}
          </Typography>
        </div>
      </Tooltip>
    );
  }

  const imagesUrls = images?.content.map((image) => image.url) || [];

  return (
    <TableCell className='p-0'>
      <DriverImagesCarousel carouselImages={imagesUrls} className='h-fit' showRemoveButton={false}>
        <div className='group relative flex aspect-video w-36 items-center justify-center overflow-hidden rounded-md'>
          <Image src={imagesUrls[0]} alt={t('columns.photo_alt')} className='h-full w-full object-cover' />
          <div className='-translate-x-1/2 -translate-y-1/2 absolute top-1/2 left-1/2 flex items-center justify-center rounded-full bg-black-a7 p-3 opacity-0 transition-opacity group-hover:opacity-100'>
            <Icon name='images' size='md' color='white' />
          </div>
        </div>
      </DriverImagesCarousel>
    </TableCell>
  );
};

export default PhotosColumn;
